import { UseFormSetValue, UseFormWatch, UseFormRegister } from 'react-hook-form';
import { useEffect, useState } from 'react';

interface UseNPWPHandlersProps {
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  register: UseFormRegister<any>;
  fieldName: string;
}

export const useNPWPHandlers = ({ setValue, watch, register, fieldName }: UseNPWPHandlersProps) => {
  const [displayValue, setDisplayValue] = useState('');

  const formatNPWP = (value: string): string => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    let formatted = numbers;

    if (numbers.length === 15 || numbers.length === 16) {
      return numbers;
    }

    if (numbers.length >= 3) formatted = numbers.slice(0, 2) + '.' + numbers.slice(2);
    if (numbers.length >= 6) formatted = numbers.slice(0, 2) + '.' + numbers.slice(2, 5) + '.' + numbers.slice(5);
    if (numbers.length >= 9)
      formatted = numbers.slice(0, 2) + '.' + numbers.slice(2, 5) + '.' + numbers.slice(5, 8) + '.' + numbers.slice(8);
    if (numbers.length >= 10)
      formatted =
        numbers.slice(0, 2) +
        '.' +
        numbers.slice(2, 5) +
        '.' +
        numbers.slice(5, 8) +
        '.' +
        numbers.slice(8, 9) +
        '-' +
        numbers.slice(9);
    if (numbers.length >= 13)
      formatted =
        numbers.slice(0, 2) +
        '.' +
        numbers.slice(2, 5) +
        '.' +
        numbers.slice(5, 8) +
        '.' +
        numbers.slice(8, 9) +
        '-' +
        numbers.slice(9, 12) +
        '.' +
        numbers.slice(12);

    return formatted;
  };

  // Custom validation function
  const validateNPWP = (value: string): string | boolean => {
    const cleanValue = value.replace(/\D/g, '');

    if (value === '') return true;

    if (cleanValue.length < 15) {
      return `NPWP minimal 15 digit (saat ini ${cleanValue.length} digit)`;
    }

    if (cleanValue.length > 16) {
      return `NPWP maksimal 16 digit (saat ini ${cleanValue.length} digit)`;
    }

    if (cleanValue.length !== 15 && cleanValue.length !== 16) {
      return 'NPWP harus tepat 15 atau 16 digit';
    }

    if (!/^[0-9]+$/.test(cleanValue)) {
      return 'NPWP hanya boleh berisi angka';
    }

    return true;
  };

  // Register with proper validation
  const registration = register(fieldName, {
    validate: validateNPWP
  });

  const formValue = watch(fieldName);
  useEffect(() => {
    setDisplayValue(formatNPWP(formValue || ''));
  }, [formValue]);

  const handleNPWPChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const numbersOnly = inputValue.replace(/\D/g, '');

    if (numbersOnly.length <= 16) {
      setDisplayValue(formatNPWP(numbersOnly));
      setValue(fieldName, numbersOnly, { shouldValidate: true }); // Important: shouldValidate: true
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      [8, 9, 27, 13, 46, 37, 39, 35, 36].includes(event.keyCode) ||
      (event.ctrlKey && [65, 67, 86, 88].includes(event.keyCode))
    ) {
      return;
    }
    if ((event.shiftKey || event.keyCode < 48 || event.keyCode > 57) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  };

  return {
    handleNPWPChange,
    handleKeyDown,
    displayValue,
    registration, // Return the registration object
    formatNPWP
  };
};
